import type { DomainDataProfile, UniversalGraph } from '../../core/data-profiles';
import type { GrcNodeType, GrcEdgeType } from '../../lib/graph/types';

/**
 * Minimal universal-node prop shape for GRC records. The canonical
 * definition lives in docs/universal-ontology.md §GRC Entity Details;
 * this interface is the subset we ingest via importers today.
 * Additional typed packs (grc-risk / grc-evidence / grc-finding / etc.)
 * slot in as extra fields over time.
 */
export interface GrcUniversalNodeProps {
  id?: string;
  controlId?: string;
  framework?: string;
  title?: string;
  description?: string;
  category?: string;
  severity?: string;
  complianceStatus?: string;
  // Pack-driven extras per docs/universal-ontology.md (non-exhaustive here).
  [key: string]: unknown;
}

export interface GrcUniversalEdgeProps {
  [key: string]: unknown;
}

/**
 * Pass-through hydrator. GRC records live primarily in `folder_records`
 * and surface through the Compass audit + M6a Controls Library panel;
 * they don't yet have a dedicated canvas shape set. If/when a
 * controls-map diagram ships, the hydrator below grows `fromUniversal`
 * that maps Control/Risk nodes to shapes.
 */
const grcHydrator: DomainDataProfile<
  GrcNodeType,
  GrcEdgeType,
  GrcUniversalNodeProps,
  GrcUniversalEdgeProps
>['hydrator'] = {
  toUniversal: (): UniversalGraph<
    GrcNodeType,
    GrcEdgeType,
    GrcUniversalNodeProps,
    GrcUniversalEdgeProps
  > => ({ nodes: [], edges: [] }),
  fromUniversal: () => ({ nodes: [], edges: [] }),
};

/**
 * GRC control catalog profile.
 *
 * Registers the `grc-control` domain so:
 *   - Folders can declare `domain='grc-control'` (GRC Catalog kind).
 *   - The ImportSourceModal picker filters connectors via
 *     `compatibleProfileIds: ['grc-control']` (GRC Register today; OSCAL +
 *     Archer / Vanta / Drata stubs land in M6a).
 *   - FolderRecordRepository rows land under profileId='grc-control'.
 *
 * Full field-pack treatment (grc-risk, grc-obligation, grc-evidence,
 * grc-finding, grc-vendor, grc-framework, grc-capability) ships with
 * M6a when the `src/domains/grc-catalog` domain lands. This profile is
 * the registration anchor that unblocks folder kinds + picker wiring.
 *
 * See plans/archive/compass/m5c-profiles-and-folder-kinds-plan-archived.md §GRCControlProfile
 * and plans/unified-data-architecture-plan.md §M5 GRC.
 */
export const GRCControlProfile: DomainDataProfile<
  GrcNodeType,
  GrcEdgeType,
  GrcUniversalNodeProps,
  GrcUniversalEdgeProps
> = {
  domainId: 'grc-control',
  profileVersion: '1.0',
  // GRC records don't have a dedicated canvas diagram type today —
  // they render via the Compass audit panel + Controls Library
  // repository panel (M6a). Map to 'generic' so the profile is reachable
  // from picker filtering; a dedicated 'controls-map' DiagramType can be
  // added later when the controls-map view ships.
  diagramTypes: ['generic'],
  nodeTypes: [
    'control',
    'risk',
    'obligation',
    'evidence',
    'finding',
    'control-framework',
    'vendor',
    'policy',
    'access_grant',
  ],
  edgeTypes: [
    'controlled_by',
    'mitigates',
    'satisfies',
    'evidenced_by',
    'governs',
    'owns_control',
    'assessed_at',
    'finding_from',
    'remediates',
    'framework_contains',
    'provided_by',
    'vendor_assessed_by',
    'granted_to',
  ],

  vocabulary: {
    recordSingular: 'Control',
    recordPlural: 'Controls',
    repositoryLabel: 'Controls Library',
    hierarchyLabel: 'Framework',
    importAction: 'Import Controls',
    keyColumnHint: 'Control ID',
    commitLabel: 'Push to GRC Tool',
    pullLabel: 'Refresh Catalog',
    diagramLabel: 'controls map',
  },

  fieldAliases: {
    id: ['id', 'ID', 'control_id', 'controlId', 'control_number', 'cc_id'],
    controlId: ['control_id', 'controlId', 'ControlId', 'control_ref', 'reference_id'],
    framework: [
      'framework',
      'Framework',
      'standard',
      'compliance_framework',
      'control_framework',
    ],
    title: ['title', 'Title', 'name', 'Name', 'control_title', 'label'],
    description: ['description', 'Description', 'desc', 'control_description', 'requirement'],
    category: ['category', 'Category', 'family', 'control_family', 'domain'],
    severity: ['severity', 'Severity', 'impact', 'risk_level', 'criticality'],
    complianceStatus: [
      'complianceStatus',
      'compliance_status',
      'status',
      'state',
      'assessment_result',
    ],
  },

  requiredProps: ['id', 'framework', 'title'],
  recommendedProps: ['controlId', 'description', 'category'],

  hierarchyConfig: {
    type: 'single-parent',
    parentProp: 'framework',
    edgeSubtype: 'framework_contains',
  },

  primaryShapeType: 'control',
  propsToAttrsMapping: {
    id: 'id',
    controlId: 'controlId',
    framework: 'framework',
    title: 'title',
    description: 'description',
    category: 'category',
    severity: 'severity',
    complianceStatus: 'complianceStatus',
  },

  hydrator: grcHydrator,

  // GRC records import read-only from external tools by default —
  // write-back is a separate integration (Archer/Vanta/Drata) tracked
  // alongside the M6a connector work.
  shapeBinding: {
    onNewEntityShape: 'diagram-only',
    onReadOnlySource: 'diagram-only',
  },

  chromeConfig: {
    hiddenTools: [],
    searchTarget: 'controls',
  },

  onboardingModes: ['blank', 'capture'],
};
